import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { FloatButton, Input, Tag } from "antd";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import trans from "../../../utils";
import { formatDateString } from "../../../JsHelpers/DateFormat";
import { popupcontext } from "../../../context/Popupscontext";
import { filtrationcontext } from "../../../context/Filtercontext";
import { useGetAllIRsQuery } from "../../../Redux/service/IR/IR";
import Loader from "../../Helpers/Loader/Loader";
import SnagLocationSelectorModal from "../../snagLocation/SnagLocationSelectorModal";
import InspectionRequestForm from "../InspectionRequestForm/InspectionRequestForm";
import Display from "../../Helpers/Table/Table Actions/Display";
import "../../../Styles/Mobile/irMobile.css";

const PAGE_SIZE = 20;
const statusColors = {
  CLOSED: "blue",
  PENDING_QA: "orange",
  PENDING_CONSULTANT: "orange",
  PENDING_REVIEW: "orange",
  QA_APPROVED: "green",
  APPROVED: "green",
  CONSULTANT_APPROVED: "green",
  QA_REJECTED: "red",
  CONSULTANT_REJECTED: "red",
  REJECTED: "red",
  CONSULTANT_APPROVED_WITH_COMMENTS: "purple",
  APPROVED_WITH_COMMENTS: "purple",
  DRAFT: "default",
  CANCELLED: "default",
};

export default function IrMobile({ userRole }) {
  const {
    handleShow,
    showDetails,
    setReloadData,
    reloadData,
    form,
    currentIRId,
  } = useContext(popupcontext);
  const { searchQuery, setSearchQuery } = useContext(filtrationcontext);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [preselectedLocation, setPreselectedLocation] = useState(null);
  const [page, setPage] = useState(1);
  const [accumulated, setAccumulated] = useState([]);
  const loadingMoreRef = useRef(false);
  const sentinelRef = useRef(null);

  const { data, isLoading, isFetching } = useGetAllIRsQuery(
    {
      page,
      limit: PAGE_SIZE,
      search: searchQuery,
      status: null,
    },
    { skip: !page },
  );

  const total = data?.pagination?.total ?? 0;
  const hasMore = accumulated.length < total;

  useEffect(() => {
    if (!data?.data) return;
    if (page === 1) {
      setAccumulated(data.data);
    } else {
      setAccumulated((prev) => [...prev, ...(data.data || [])]);
    }
    loadingMoreRef.current = false;
  }, [data?.data, page]);

  useEffect(() => {
    setPage(1);
    setAccumulated([]);
  }, [searchQuery]);

  useEffect(() => {
    if (reloadData) {
      setPage(1);
      setAccumulated([]);
      setReloadData(false);
    }
  }, [reloadData, setReloadData]);

  useEffect(() => {
    if (!form && preselectedLocation) {
      setPreselectedLocation(null);
    }
  }, [form, preselectedLocation]);

  const loadMore = useCallback(() => {
    if (loadingMoreRef.current || !hasMore || isFetching) return;
    loadingMoreRef.current = true;
    setPage((p) => p + 1);
  }, [hasMore, isFetching]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore || isFetching) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { root: null, rootMargin: "100px", threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore, hasMore, isFetching]);

  const handleConfirmLocation = (locationData) => {
    setPreselectedLocation(locationData);
    setLocationModalOpen(false);
    handleShow("addForm");
  };

  const getStatus = (record) => {
    const value =
      record?.status_info?.value || record?.status || record?.original_status;
    const label =
      record?.status_info?.label || record?.status || record?.original_status;
    return { value, label };
  };

  const handleCardClick = (record) => {
    showDetails("IR", record.id);
  };

  if (isLoading && page === 1) {
    return (
      <div className="ir-mobile-loader">
        <Loader />
      </div>
    );
  }

  return (
    <div className="ir-mobile">
      <div className="ir-mobile-scroll">
        <Input
          prefix={<SearchOutlined style={{ color: "#aaa" }} />}
          placeholder={trans("Search")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="ir-mobile-search"
        />
        <div className="ir-mobile-cards">
          {accumulated.length === 0 && !isFetching ? (
            <div className="ir-mobile-empty">
              {trans("No inspection requests")}
            </div>
          ) : (
            accumulated.map((record) => {
              const { value: statusValue, label: statusLabel } =
                getStatus(record);
              return (
                <div
                  key={record.id}
                  className="ir-mobile-card"
                  onClick={() => handleCardClick(record)}
                >
                  <div className="ir-mobile-card-header">
                    <span className="ir-mobile-card-number">
                      {record.ir_number}
                    </span>
                    <Tag color={statusColors[statusValue] || "default"}>
                      {trans(statusLabel)}
                    </Tag>
                  </div>
                  <div className="ir-mobile-card-body">
                    <div className="ir-mobile-card-row">
                      <span className="ir-mobile-card-label">
                        {trans("Project")}
                      </span>
                      <span>{record?.project?.name ?? "—"}</span>
                    </div>
                    <div className="ir-mobile-card-row">
                      <span className="ir-mobile-card-label">
                        {trans("Discipline")}
                      </span>
                      <span>{trans(record?.discipline ?? "—")}</span>
                    </div>
                    <div className="ir-mobile-card-row">
                      <span className="ir-mobile-card-label">
                        {trans("Zone")}
                      </span>
                      <span>{record?.zone?.name ?? "—"}</span>
                    </div>
                    <div className="ir-mobile-card-row">
                      <span className="ir-mobile-card-label">
                        {trans("Level")}
                      </span>
                      <span>{record?.level?.name ?? "—"}</span>
                    </div>
                    <div className="ir-mobile-card-row">
                      <span className="ir-mobile-card-label">
                        {trans("Room")}
                      </span>
                      <span>{record?.room?.name ?? "—"}</span>
                    </div>
                    <div className="ir-mobile-card-row">
                      <span className="ir-mobile-card-label">
                        {trans("Ready Date")}
                      </span>
                      <span>{record?.ready_date ?? "—"}</span>
                    </div>
                    <div className="ir-mobile-card-row">
                      <span className="ir-mobile-card-label">
                        {trans("Submitted By")}
                      </span>
                      <span>{record?.submittedBy?.name ?? "—"}</span>
                    </div>
                    <div className="ir-mobile-card-row">
                      <span className="ir-mobile-card-label">
                        {trans("Submitted At")}
                      </span>
                      <span>{formatDateString(record?.submitted_at)}</span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
        <div ref={sentinelRef} className="ir-mobile-sentinel" />
        {isFetching && page > 1 && (
          <div className="ir-mobile-loading-more">
            <Loader />
          </div>
        )}
      </div>
      {userRole === "contractor" && (
        <FloatButton
          type="primary"
          icon={<PlusOutlined />}
          className="ir-mobile-fab"
          onClick={() => setLocationModalOpen(true)}
          // tooltip={trans("Add new IR")}
        />
      )}
      <SnagLocationSelectorModal
        open={locationModalOpen}
        onClose={() => setLocationModalOpen(false)}
        onConfirm={handleConfirmLocation}
      />
      <InspectionRequestForm preselectedLocation={preselectedLocation} />
      {currentIRId && <Display irId={currentIRId} />}
    </div>
  );
}
